<script setup>
import { onMounted, provide, ref, useTemplateRef, watch } from "vue";
import DigitalMazeSvg from '@/assets/digital-maze.svg?url'
import QuoteSvg from '@/assets/quote.svg?skipsvgo'
import { useSlider } from "@/composeables/useSlider";
import { usePointerSwipe } from '@vueuse/core'

const props = defineProps({});

const testimonials = ref([]);
function register(testimonial) {
    testimonials.value.push(testimonial);
}
function unregister(testimonial) {
    testimonials.value = testimonials.value.filter(
        ({ uid }) => testimonial.uid !== uid,
    );
}
provide("testimonial-slider", { register, unregister });

const { direction, slideNext, slidePrev, canNext, canPrev, current } = useSlider(testimonials)

const walzTransition = ref('walz-up')
const slideTransition = ref('slide-right')
watch(direction, (direction) => {
    walzTransition.value = direction >= 0 ? 'walz-up' : 'walz-down'
    slideTransition.value = direction >= 0 ? 'slide-right' : 'slide-left'
})

const textsRef = useTemplateRef('textsRef')
const { isSwiping: isTextsSwiping, direction: swipeTextsDirection } = usePointerSwipe(textsRef, {
    passive: true,
    threshold: 10,
})
watch([isTextsSwiping, swipeTextsDirection], ([isSwiping, swipeDirection], [isSwipingOld]) => {
    if (!isSwiping && isSwipingOld) {
        switch (swipeDirection) {
            case 'up': slideNext(); break;
            case 'down': slidePrev(); break;
            default: break;
        }
        return false
    }
})
const imagesRef = useTemplateRef('imagesRef')

const { isSwiping, direction: swipeDirection } = usePointerSwipe(imagesRef, {
    threshold: 10,
    passive: true,
})
watch([isSwiping, swipeDirection], ([isSwiping, swipeDirection], [isSwipingOld]) => {
    if (!isSwiping && isSwipingOld) {
        switch (swipeDirection) {
            case 'left': slideNext(); break;
            case 'right': slidePrev(); break;
            default: break;
        }
        return false
    }
})

const isMounted = ref(false)
onMounted(() => {
    requestAnimationFrame(() => { isMounted.value = true })
})
</script>

<template>
        <button
            @click="slidePrev"
            :class="[ 'section-testimonial-slider-big__button-prev' ]"
            :disabled="!canPrev()"
        >
            <slot name="slide-prev"></slot>
        </button>

        <button
            @click="slideNext"
            :class="[ 'section-testimonial-slider-big__button-next' ]"
            :disabled="!canNext()"
        >
            <slot name="slide-next"></slot>
        </button>

        <div
            :class="['section-testimonial-slider-big__images']"
            ref="imagesRef"
        >
            <img class="section-testimonial-slider-big__decor" :src="DigitalMazeSvg" />

            <QuoteSvg class="section-testimonial-slider-big__quote" />
            <div class="section-testimonial-slider-big__images-transition-wrapper">
                <transition
                    class="section-testimonial-slider-big__images-transition"
                    :name="slideTransition"
                    :css="isMounted"
                >
                    <div :key="current?.uid">
                        <div class="section-testimonial-slider-big__images-wrapper">
                            <component
                                :is="vNode"
                                :key="index"
                                v-for="(vNode, index) in current?.image ?? []"
                            />
                        </div>
                    </div>
                </transition>
            </div>
        </div>

        <div
            class="section-testimonial-slider-big__texts fg-white"
            ref="textsRef"
        >
            <div class="section-testimonial-slider-big__texts-transition-wrapper">
                <transition
                    class="section-testimonial-slider-big__texts-transition"
                    :name="walzTransition"
                    :css="isMounted"
                >
                    <div :key="current?.uid">
                        <div class="section-testimonial-slider-big__texts-wrapper">
                            <div class="section-testimonial-slider-big__texts-quote">
                                <component
                                    :is="vNode"
                                    :key="index"
                                    v-for="(vNode, index) in current?.text ?? []"
                                />
                            </div>

                            <p class="section-testimonial-slider-big__texts-author">
                                {{ current?.fullName }} | {{ current?.jobTitle }}
                            </p>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    <slot />
</template>

<style lang="scss">
@use "@/scss/framework" as *;
@layer sections.section;

@layer sections.testimonial-slider-big {

    :root {
        --section-testimonial-slider-big-images-v-padding: #{clamped(25px, 50px)};
        --section-testimonial-slider-big-images-h-padding: #{clamped(19px, 38px)};
        --section-testimonial-slider-big-icon-color: var(--color-radical-red);
        --section-testimonial-slider-big-icon-color--hover: var(--color-radical-red--hover);
        --section-testimonial-slider-transition-duration: var(--duration-super-slow);
        --section-testimonial-slider-easing: var(--easing-back-out);
        --section-testimonial-slider-big-quote-text-font: 800 #{clamped(18px, 40px)} / 1.1 var(--font-family-headline);
        --section-testimonial-slider-big-author-font: var(--font-body-1);
    }

    .section-testimonial-slider-big {
        --padding-behavior-top: var(--dimension-large);
        --padding-behavior-bottom: var(--dimension-large);

        position: relative;
        padding-top: var(--padding-behavior-top);
        padding-bottom: var(--padding-behavior-bottom);

        background-color: var(--background-color);
        background-image: var(--background-image);
        background-size: cover;
        background-repeat: no-repeat;
        contain: paint;

        grid-template-rows:
                [buttons-start] 0px
                [images-start] auto [images-end buttons-end]
                42px
                [texts-start] auto [texts-end]
            ;

        @include mq-from(sm) {
            grid-template-rows:
                [buttons-start texts-start images-start] 1fr [images-end texts-end buttons-end]
            ;
        }

        &__bg {
            z-index: 1;
            img {
                object-fit: cover;
                width: 100%;
                height: 100%;
            }
        }


        &__texts {
            z-index: 4;
            grid-row: texts;
            display: grid;
            user-select: none;
            cursor: grab;

            @include mq-to(sm) {
                grid-template-columns: subgrid;
                grid-column: grid;
                align-self: end;
                touch-action: auto !important;
            }

            @include mq-from(sm) { grid-column: column-start 6 / column-end 11; }
            @include mq-from(xl) { grid-column: column-start 6 / column-end 11; }

            &-transition-wrapper {
                display: grid;
                grid-template-areas: "content";
                grid-column: grid;
                & > * {
                    grid-area: content;
                }
            }

            &-transition {
                display: grid;
            }

            &-wrapper {
                align-self: center;
                display: grid;
                :first-child { margin-top: 0; }
                :last-child { margin-bottom: 0; }
            }

            &-quote {
                font: var(--section-testimonial-slider-big-quote-text-font);
                :is(h1,h2,h3,h4,h5,h6) {
                    font: inherit;
                }
            }
            &-author {
                margin-top: var(--size-20);
                font: var(--section-testimonial-slider-big-author-font);
                :is(h1,h2,h3,h4,h5,h6) {
                    font: inherit;
                }
            }
        }

        &__images {
            background-image: var(--background-image);
            background-repeat: no-repeat;
            background-position: top left;
            background-size: cover;

            touch-action: pan-y !important;

            z-index: 2;
            display: grid;
            align-self: center;
            position: relative;
            user-select: none;
            grid-template-columns:
                [decor-start] var(--section-testimonial-slider-big-images-h-padding)
                [image-start] 1fr [decor-end]
                var(--section-testimonial-slider-big-images-h-padding) [image-end];
            grid-template-rows:
                [decor-start] var(--section-testimonial-slider-big-images-v-padding)
                [image-start] 1fr [image-end]
                var(--section-testimonial-slider-big-images-v-padding) [decor-end];

            @include mq-from(sm) {
                grid-template-columns:
                    [decor-start image-start] 1fr [decor-end image-end];
            }


            grid-row: images;
            grid-column: grid;
            @include mq-from(sm) { grid-column: column-start 2 / column-end 4; }
            @include mq-from(xl) { grid-column: column-start 2 / column-end 4; }

            &-transition-wrapper {
                grid-area: image;
                overflow: clip;
                display: grid;
                grid-template-areas: "content";
                cursor: grab;
                // place-content: center;
                & > * {
                    grid-area: content;
                }

                @include mq-from(sm) {
                    transform: translateX(var(--section-testimonial-slider-big-images-v-padding));
                }
            }

            &-transition {
                transform-origin: left center;
            }

            &-wrapper {
                position: relative;
            }
        }

        &__decor {
            grid-area: decor;
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &__quote {
            z-index: 4;
            position: absolute;
            top: var(--section-testimonial-slider-big-images-v-padding);
            right: 0;
            width: clamp(15%, 12vw, 25%);
            height: auto;

            transform: translateX(50%) translateY(33%);
            @include mq-from(sm) {
                transform: translateX(50%) translateX(var(--section-testimonial-slider-big-images-v-padding)) translateY(33%);
            }
        }

        &__image {
            z-index: 3;
            width: 100%;
            aspect-ratio: 42 / 52;

            &.media img {
                height: 100%;
                object-fit: cover;
            }
        }

        button {
            appearance: none;
            border: none;
            background: none;
            padding: 0;
            z-index: 5;

            svg {
                @include svg-auto-color;
                color: var(--section-testimonial-slider-big-icon-color);
                width: 100%;
            }
            @include hover {
                svg {
                    color: var(--section-testimonial-slider-big-icon-color--hover);
                }
            }
            &:disabled {
                opacity: 0.3;
            }
        }

        &__button-prev {
            grid-row: buttons;
            width: clamped(20px, 34px);

            place-self: center;
            grid-column: viewport-start / grid-start;
            @include mq-from(sm) {
                place-self: center start;
                grid-column: column-start 1 / column-end 1;
            }
        }

        &__button-next {
            grid-row: buttons;
            width: clamped(20px, 34px);

            place-self: center;
            grid-column: grid-end / viewport-end;
            @include mq-from(sm) {
                place-self: center end;
                grid-column: column-start 12 / column-end 12;
            }
        }
    }


    .walz-up-leave-active {
        backface-visibility: hidden;
        animation: walz-up-leave var(--section-testimonial-slider-transition-duration) var(--section-testimonial-slider-easing) forwards;
    }
    .walz-up-enter-active {
        backface-visibility: hidden;
        animation: walz-up-enter var(--section-testimonial-slider-transition-duration) var(--section-testimonial-slider-easing) forwards;
    }
    .walz-down-leave-active {
        backface-visibility: hidden;
        animation: walz-down-leave var(--section-testimonial-slider-transition-duration) var(--section-testimonial-slider-easing) forwards;
    }
    .walz-down-enter-active {
        backface-visibility: hidden;
        animation: walz-down-enter var(--section-testimonial-slider-transition-duration) var(--section-testimonial-slider-easing) forwards;
    }

    @keyframes walz-up-leave {
        0% { transform: translateY(0%) rotateX(0turn) scale(1); opacity: 1; }
        90% { opacity: 0; }
        100% { transform: translateY(-100%) rotateX(0.25turn) scale(0.9); opacity: 0; }
    }
    @keyframes walz-up-enter {
        0% { transform: translateY(100%) rotateX(-0.25turn) scale(0.9); opacity: 0; }
        90% { opacity: 1; }
        100% { transform: translateY(0%) rotateX(0turn) scale(1); opacity: 1; }
    }

    @keyframes walz-down-leave {
        0% { transform: translateY(0%) rotateX(0turn) scale(1); opacity: 1; }
        90% { opacity: 0; }
        100% { transform: translateY(100%) rotateX(-0.25turn) scale(0.9); opacity: 0; }
    }
    @keyframes walz-down-enter {
        0% { transform: translateY(-100%) rotateX(0.25turn) scale(0.9); opacity: 0; }
        90% { opacity: 1; }
        100% { transform: translateY(0%) rotateX(0turn) scale(1); opacity: 1; }
    }

    .slide-left-leave-active {
        animation: slide-left-leave var(--section-testimonial-slider-transition-duration) var(--section-testimonial-slider-easing) forwards;
    }
    .slide-left-enter-active {
        animation: slide-left-enter var(--section-testimonial-slider-transition-duration) var(--section-testimonial-slider-easing) forwards;
    }
    .slide-right-leave-active {
        animation: slide-right-leave var(--section-testimonial-slider-transition-duration) var(--section-testimonial-slider-easing) forwards;
    }
    .slide-right-enter-active {
        animation: slide-right-enter var(--section-testimonial-slider-transition-duration) var(--section-testimonial-slider-easing) forwards;
    }

    @keyframes slide-left-enter {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(0%); }
    }
    @keyframes slide-left-leave {
        0% { transform: translateX(0%); }
        100% { transform: translateX(100%); }
    }
    @keyframes slide-right-enter {
        0% { transform: translateX(100%); }
        100% { transform: translateX(0%); }
    }
    @keyframes slide-right-leave {
        0% { transform: translateX(0%); }
        100% { transform: translateX(-100%); }
    }
}
</style>
